import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Sun,
  Moon,
  Monitor,
  Languages,
  LogOut,
  Key,
  Loader2,
  ChevronsUpDown,
  Check,
  Shield
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from "@/components/ui/sidebar";
import { useNavigate } from "@tanstack/react-router";
import { useThemeStore } from "@/stores/theme.store";
import { useAuthStore } from "@/stores/auth.store";
import { changeLanguage, getCurrentLanguage } from "@/lib/i18n";
import { api, ApiException } from "@/lib/api";
import pkg from "../../../../../../package.json";

export function SidebarUserMenu() {
  const { t } = useTranslation();
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const { isMobile } = useSidebar();
  const navigate = useNavigate();
  const currentLang = getCurrentLanguage();

  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const themeIcon = {
    light: <Sun className="size-4" />,
    dark: <Moon className="size-4" />,
    system: <Monitor className="size-4" />
  };

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword) return;

    setIsChangingPassword(true);
    try {
      await api.post("/auth/change-password", { oldPassword, newPassword });
      toast.success(t("header.passwordChanged"));
      setIsPasswordDialogOpen(false);
      setOldPassword("");
      setNewPassword("");
    } catch (error) {
      if (error instanceof ApiException) {
        toast.error(t("header.passwordChangeError"), {
          description: error.message
        });
      } else {
        toast.error(t("header.passwordChangeError"));
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setOldPassword("");
      setNewPassword("");
    }
    setIsPasswordDialogOpen(open);
  };

  if (!user) return null;

  const userInitial = user.name?.charAt(0).toUpperCase() || "U";

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground text-sm font-medium">
                  {userInitial}
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {user.email}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            >
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">
                      v{pkg.version}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />

              {(user.role === "MODERATOR" || user.role === "ADMIN") && (
                <>
                  <DropdownMenuItem onClick={() => navigate({ to: "/admin" })}>
                    <Shield className="mr-2 size-4" />
                    {t("header.admin")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}

              {isMobile ? (
                <>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    {t("header.theme")}
                  </DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => setTheme("light")}>
                    <Sun className="mr-2 size-4" />
                    {t("header.themeLight")}
                    {theme === "light" && <Check className="ml-auto size-4" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("dark")}>
                    <Moon className="mr-2 size-4" />
                    {t("header.themeDark")}
                    {theme === "dark" && <Check className="ml-auto size-4" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("system")}>
                    <Monitor className="mr-2 size-4" />
                    {t("header.themeSystem")}
                    {theme === "system" && <Check className="ml-auto size-4" />}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    {t("header.language")}
                  </DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => changeLanguage("de")}>
                    Deutsch
                    {currentLang === "de" && (
                      <Check className="ml-auto size-4" />
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => changeLanguage("en")}>
                    English
                    {currentLang === "en" && (
                      <Check className="ml-auto size-4" />
                    )}
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      {themeIcon[theme]}
                      <span className="ml-2">{t("header.theme")}</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem onClick={() => setTheme("light")}>
                        <Sun className="mr-2 size-4" />
                        {t("header.themeLight")}
                        {theme === "light" && (
                          <Check className="ml-auto size-4" />
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setTheme("dark")}>
                        <Moon className="mr-2 size-4" />
                        {t("header.themeDark")}
                        {theme === "dark" && (
                          <Check className="ml-auto size-4" />
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setTheme("system")}>
                        <Monitor className="mr-2 size-4" />
                        {t("header.themeSystem")}
                        {theme === "system" && (
                          <Check className="ml-auto size-4" />
                        )}
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>

                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <Languages className="size-4" />
                      <span className="ml-2">{t("header.language")}</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem onClick={() => changeLanguage("de")}>
                        Deutsch
                        {currentLang === "de" && (
                          <Check className="ml-auto size-4" />
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => changeLanguage("en")}>
                        English
                        {currentLang === "en" && (
                          <Check className="ml-auto size-4" />
                        )}
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </>
              )}

              <DropdownMenuSeparator />

              <DropdownMenuItem onClick={() => setIsPasswordDialogOpen(true)}>
                <Key className="mr-2 size-4" />
                {t("header.changePassword")}
              </DropdownMenuItem>

              <DropdownMenuItem onClick={logout} className="text-destructive">
                <LogOut className="mr-2 size-4" />
                {t("header.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      {/* Change Password Dialog */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("header.changePassword")}</DialogTitle>
            <DialogDescription>
              {t("header.oldPassword")} &amp; {t("header.newPassword")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="oldPassword">{t("header.oldPassword")}</Label>
              <Input
                id="oldPassword"
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                disabled={isChangingPassword}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">{t("header.newPassword")}</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isChangingPassword}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleDialogClose(false)}
              disabled={isChangingPassword}
            >
              {t("header.cancel")}
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={isChangingPassword || !oldPassword || !newPassword}
            >
              {isChangingPassword ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : null}
              {t("header.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
